import { apiResultSchema, type ApiError } from '@ai-sales/shared'
import type { ZodType } from 'zod'

/**
 * Единственная точка обращения к нашему API.
 *
 * Весь смысл — в том, что ответ не разбирается руками ни в одном экране.
 * Схема приходит из пакета shared, то есть та же самая, которой сервер этот
 * ответ формировал. Экран получает либо готовые типизированные данные, либо
 * ошибку заданной формы; случая «пришло что-то похожее на данные» не бывает.
 */

export const REQUEST_ID_HEADER = 'x-request-id'

/**
 * Ошибка обращения к API.
 *
 * Идентификатор запроса хранится отдельным полем и показывается человеку.
 * Это мелочь, которая решает главную проблему поддержки: вместо «у меня
 * что-то не сохранилось» приходит строка, по которой в логах находится ровно
 * этот запрос и все порождённые им фоновые задачи.
 */
export class ApiRequestError extends Error {
  readonly code: string
  readonly status: number
  readonly requestId: string | null
  readonly details: unknown

  constructor(init: {
    code: string
    message: string
    status: number
    requestId?: string | null
    details?: unknown
  }) {
    super(init.message)
    this.name = 'ApiRequestError'
    this.code = init.code
    this.status = init.status
    this.requestId = init.requestId ?? null
    this.details = init.details
  }

  /** Сессия истекла или её нет — повод отправить человека на вход. */
  get isUnauthorized(): boolean {
    return this.status === 401
  }

  get isForbidden(): boolean {
    return this.status === 403
  }

  get isNotFound(): boolean {
    return this.status === 404
  }
}

export interface RequestOptions<TBody> {
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE'
  body?: TBody
  /** Параметры строки запроса. Пустые и undefined отбрасываются. */
  query?: Record<string, string | number | boolean | undefined | null>
  signal?: AbortSignal
}

function buildUrl(path: string, query: RequestOptions<unknown>['query']): string {
  const url = `/api${path}`

  if (!query) return url

  const params = new URLSearchParams()

  for (const [key, value] of Object.entries(query)) {
    if (value === undefined || value === null || value === '') continue
    params.set(key, String(value))
  }

  const serialized = params.toString()

  return serialized ? `${url}?${serialized}` : url
}

/**
 * Выполняет запрос и разбирает ответ схемой полезной нагрузки.
 *
 * Ошибки всех видов приводятся к одному типу. Отдельно обрабатывается случай,
 * когда ответ вообще не разбирается как JSON: это чаще всего означает, что
 * запрос до API не дошёл — упал прокси или backend не запущен, — и человеку
 * нужно сказать именно это, а не «unexpected token < in JSON».
 */
export async function apiRequest<TPayload, TBody = undefined>(
  path: string,
  schema: ZodType<TPayload>,
  options: RequestOptions<TBody> = {},
): Promise<TPayload> {
  const method = options.method ?? 'GET'

  let response: Response

  try {
    response = await fetch(buildUrl(path, options.query), {
      method,
      // Сессия живёт в httpOnly-cookie, поэтому запрос обязан её приложить
      credentials: 'same-origin',
      headers: options.body === undefined ? {} : { 'Content-Type': 'application/json' },
      ...(options.body === undefined ? {} : { body: JSON.stringify(options.body) }),
      ...(options.signal ? { signal: options.signal } : {}),
    })
  } catch (cause) {
    throw new ApiRequestError({
      code: 'NETWORK_ERROR',
      message: 'Не удалось связаться с сервером. Запущен ли backend?',
      status: 0,
      details: cause,
    })
  }

  const requestId = response.headers.get(REQUEST_ID_HEADER)

  let raw: unknown

  try {
    raw = await response.json()
  } catch {
    throw new ApiRequestError({
      code: 'BAD_RESPONSE',
      message: `Сервер ответил кодом ${response.status}, но не вернул JSON`,
      status: response.status,
      requestId,
    })
  }

  const envelope = apiResultSchema(schema).safeParse(raw)

  if (!envelope.success) {
    /**
     * Ответ не соответствует контракту. Это не ошибка данных, а расхождение
     * версий — фронтенд и backend собраны из разного кода. Формулировка
     * прямая, потому что лечится это развёртыванием, а не действиями
     * пользователя.
     */
    throw new ApiRequestError({
      code: 'CONTRACT_MISMATCH',
      message: 'Ответ сервера не соответствует ожидаемому формату. Похоже, версии не совпадают.',
      status: response.status,
      requestId,
      details: envelope.error.issues,
    })
  }

  if (!envelope.data.success) {
    const error: ApiError = envelope.data.error

    throw new ApiRequestError({
      code: error.code,
      message: error.message,
      status: response.status,
      requestId: error.requestId ?? requestId,
      details: error.details,
    })
  }

  return envelope.data.data
}

/** Приводит что угодно к тексту, пригодному для показа человеку. */
export function errorMessage(error: unknown): string {
  if (error instanceof ApiRequestError) return error.message
  if (error instanceof Error) return error.message
  return 'Неизвестная ошибка'
}

export function errorRequestId(error: unknown): string | null {
  return error instanceof ApiRequestError ? error.requestId : null
}
